import { useEffect, useRef } from "react";
import L from "leaflet";
import "leaflet/dist/leaflet.css";
import { MapPinned } from "lucide-react";

const MILES_TO_METERS = 1609.34;

const colorFor = (areaId, selectedId, recommendedId) => {
  if (areaId === selectedId) return "#0052FF";
  if (areaId === recommendedId) return "#059669";
  return "#94A3B8";
};

export const LeadServiceAreaMap = ({ matches, customer, selectedId, recommendedId, testId = "lead-service-area-map" }) => {
  const elRef = useRef(null);
  const mapRef = useRef(null);

  useEffect(() => {
    if (!elRef.current || !customer) return;
    let cancelled = false;
    const map = L.map(elRef.current, { attributionControl: false, zoomAnimation: false, fadeAnimation: false, markerZoomAnimation: false }).setView([customer.lat, customer.lng], 11);
    L.tileLayer("https://{s}.tile.openstreetmap.org/{z}/{x}/{y}.png", { maxZoom: 18 }).addTo(map);
    mapRef.current = map;
    const bounds = L.latLngBounds([[customer.lat, customer.lng]]);

    (matches || []).forEach((m) => {
      const preview = m.geography_preview;
      const color = colorFor(m.service_area_id, selectedId, recommendedId);
      const weight = m.service_area_id === selectedId ? 3 : 1.5;
      if (!preview) return;
      let layer;
      if (preview.kind === "radius" && preview.center) {
        const [lng, lat] = preview.center;
        layer = L.circle([lat, lng], { radius: (preview.radius_miles || 10) * MILES_TO_METERS, color, weight, fillOpacity: 0.15 });
      } else if (preview.geometry) {
        layer = L.geoJSON(preview.geometry, { style: { color, weight, fillOpacity: 0.15 } });
      }
      if (layer) {
        layer.addTo(map);
        try { bounds.extend(layer.getBounds()); } catch { /* ignore */ }
      }
    });

    L.circleMarker([customer.lat, customer.lng], { radius: 7, color: "#0F172A", weight: 2, fillColor: "#FBBF24", fillOpacity: 1 })
      .bindTooltip("Customer address", { permanent: false })
      .addTo(map);

    const t = setTimeout(() => {
      if (cancelled || !mapRef.current) return;
      try { map.invalidateSize(); if (bounds.isValid()) map.fitBounds(bounds, { padding: [24, 24], animate: false }); } catch { /* leaflet race */ }
    }, 0);

    return () => { cancelled = true; clearTimeout(t); try { map.remove(); } catch { /* noop */ } mapRef.current = null; };
  }, [customer, matches, selectedId, recommendedId]);

  if (!customer) {
    return (
      <div data-testid={testId} className="flex h-64 items-center justify-center rounded-2xl border border-dashed border-slate-200 bg-slate-50 text-xs text-slate-400 dark:border-white/10 dark:bg-white/[0.03]">
        <MapPinned size={18} className="mr-2 text-slate-300 dark:text-slate-600" /> Verify the address to see the map
      </div>
    );
  }

  return <div ref={elRef} data-testid={testId} className="h-64 w-full rounded-2xl border border-slate-200 dark:border-white/10" />;
};
